import { FormEvent, ReactElement, ReactNode, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { m, AnimatePresence } from 'framer-motion'

import Button from '@/components/Button'
import Input from '@/components/GenericInput'
import { GenericErrors } from '@/lib/definitions/generic.types'
import { cn } from '@/lib/misc/Utils'

import CloseIcon from '@icons/x-mark'
import SuccessIcon from '@icons/check-circle'
import WarningIcon from '@icons/exclamation-triangle'
import InfoIcon from '@icons/information-circle'

interface DialogProps {
  title?: string
  message?: string | ReactElement
  children?: ReactNode
  actionConfirmationMessage?: string | ReactElement
  actionConfirmationValue?: string | number
  onDismiss: () => void
  onConfirm: () => void
  dismissOnBackdrop?: boolean
  cancellable?: boolean
  hasButtons?: boolean
  disabledButtons?: boolean
  confirmationButtonDisabled?: boolean
  confirmText?: string | ReactElement
  cancelText?: string
  cy?: string
  type?: 'warning' | 'info' | 'error' | 'success'
  wrapperClassName?: string
  fullPage?: boolean
  maxWidth?: string
  scrollableBody?: boolean
  closable?: boolean
}

const Dialog = ({
  title,
  message,
  children,
  onDismiss,
  onConfirm,
  actionConfirmationMessage,
  actionConfirmationValue,
  dismissOnBackdrop = true,
  cancellable = true,
  hasButtons = true,
  disabledButtons = false,
  confirmationButtonDisabled = false,
  confirmText = 'Proceed',
  cancelText = 'Cancel',
  cy,
  type,
  wrapperClassName,
  fullPage,
  maxWidth,
  scrollableBody = false,
  closable = false,
}: DialogProps) => {
  const [confirmationInputText, setConfirmationInputText] = useState('')
  const [confirmationInputError, setConfirmationInputError] = useState<
    GenericErrors | undefined
  >()

  const icon = useMemo(() => {
    if (type === 'warning') {
      return {
        icon: <WarningIcon />,
        classes: 'text-status-warning-700 bg-status-warning/10',
      }
    }
    if (type === 'error') {
      return {
        icon: <WarningIcon />,
        classes: 'text-status-error-700 bg-status-error/10',
      }
    }
    if (type === 'info') {
      return {
        icon: <InfoIcon />,
        classes: 'text-status-info-700 bg-status-info/10',
      }
    } else if (type === 'success') {
      return {
        icon: <SuccessIcon />,
        classes: 'text-status-good-700 bg-status-good/10',
      }
    }
  }, [type])

  const handleConfirmation = () => {
    if (
      actionConfirmationValue &&
      actionConfirmationValue !== confirmationInputText
    ) {
      setConfirmationInputError({
        'confirmation-input': [
          `"${confirmationInputText}" doesn't match "${actionConfirmationValue}"`,
        ],
      })
      return
    }
    setConfirmationInputError(undefined)

    onConfirm()
  }

  const dialogContent = (
    <AnimatePresence>
      <m.div
        className={cn(
          'il-dialog flex fixed top-0 left-0 w-screen h-screen justify-center items-center z-50',
          'supports-[height:100cqh]:h-[100cqh] supports-[height:100svh]:h-svh',
          scrollableBody && 'min-h-0',
          fullPage && 'w-full h-full'
        )}
      >
        <m.div
          className="flex fixed bg-text-darker/25 w-full backdrop-blur-[3px] h-full cursor-pointer"
          onClick={() => dismissOnBackdrop && onDismiss()}
        />

        <m.div
          className={cn(
            'p-2 bg-surface/25 md:rounded-4xl shadow-popover z-999 min-h-0 min-w-0 border border-neutral-40',
            scrollableBody ? 'flex flex-col' : 'flex flex-row',
            fullPage
              ? 'w-[95%] h-[95%]'
              : scrollableBody
                ? 'w-full max-h-[calc(100svh-16px)] md:max-h-[calc(100vh-40px)] overflow-hidden'
                : 'w-full h-full md:h-min md:max-h-[calc(100vh-40px)]',
            maxWidth || 'md:max-w-[530px]'
          )}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          <div
            className={cn(
              'bg-surface p-5 rounded-3xl border border-neutral-40 shadow-card',
              scrollableBody && 'p-0',
              scrollableBody
                ? cn(
                    'flex flex-1 min-h-0 min-w-0 w-full overflow-hidden',
                    icon ? 'flex-row' : 'flex-col'
                  )
                : 'size-full',
              closable && 'relative',
              wrapperClassName
            )}
          >
            {closable && (
              <button
                type="button"
                aria-label="Close"
                className="absolute right-4 top-4 z-10 text-neutral-60 hover:text-neutral-90 transition-colors cursor-pointer"
                onClick={onDismiss}
              >
                <CloseIcon className="size-5" />
              </button>
            )}

            {!!icon && (
              <div
                className={cn(
                  'flex justify-center items-center w-11 h-11 p-3 rounded-full mr-5',
                  icon.classes
                )}
              >
                {icon.icon}
              </div>
            )}

            <div
              className={cn(
                'flex flex-col w-full',
                scrollableBody &&
                  cn(
                    'min-h-0 overflow-hidden',
                    icon ? 'flex-1 min-w-0' : 'flex-1'
                  )
              )}
            >
              {!!title && (
                <h2
                  className={cn(
                    'mb-2 mt-1 text-neutral-90 font-semibold text-lg',
                    scrollableBody && 'shrink-0 px-5 pt-5 mt-0 mb-1'
                  )}
                >
                  {title}
                </h2>
              )}
              {!!message && (
                <h4
                  className={cn(
                    'text-base text-neutral-60 whitespace-pre-line',
                    scrollableBody && 'shrink-0 px-5'
                  )}
                >
                  {message}
                </h4>
              )}
              {scrollableBody ? (
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5">
                  {children}
                  {!!actionConfirmationMessage && (
                    <>
                      <h4 className="text-base mt-6 text-accent-60">
                        {actionConfirmationMessage}
                      </h4>
                      <Input
                        name="confirmation-input"
                        value={confirmationInputText}
                        errors={confirmationInputError?.['confirmation-input']}
                        onChange={(evt: FormEvent<HTMLInputElement>) =>
                          setConfirmationInputText(evt.currentTarget.value)
                        }
                      />
                    </>
                  )}
                </div>
              ) : (
                <>
                  {children}
                  {!!actionConfirmationMessage && (
                    <>
                      <h4 className="text-base mt-6 text-accent-60">
                        {actionConfirmationMessage}
                      </h4>
                      <Input
                        name="confirmation-input"
                        value={confirmationInputText}
                        errors={confirmationInputError?.['confirmation-input']}
                        onChange={(evt: FormEvent<HTMLInputElement>) =>
                          setConfirmationInputText(evt.currentTarget.value)
                        }
                      />
                    </>
                  )}
                </>
              )}

              {hasButtons && (
                <div
                  className={cn(
                    'flex justify-end w-full mt-9 gap-2',
                    scrollableBody &&
                      'shrink-0 mt-0 border-t border-neutral-10 px-5 py-4'
                  )}
                >
                  {cancellable && (
                    <Button
                      style="normal"
                      className="text-accent-60"
                      onClick={onDismiss}
                      disabled={disabledButtons}
                    >
                      {cancelText}
                    </Button>
                  )}
                  <Button
                    style="primary"
                    onClick={handleConfirmation}
                    disabled={disabledButtons || confirmationButtonDisabled}
                    cy={cy}
                  >
                    {confirmText}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </m.div>
      </m.div>
    </AnimatePresence>
  )

  return createPortal(dialogContent, document.body)
}

export default Dialog
